import { useState, useCallback, useEffect, useRef } from 'react';
import { recordingsApi, fetchExportGenerationProgress } from '../services/recordingsApi';
import { useAuth } from '../contexts/AuthContext';

export type DownloadStatus = 'pending' | 'preparing' | 'downloading' | 'paused' | 'completed' | 'error';

export type DownloadType = 'full' | 'segment' | 'screenshot' | 'advanced';

export interface DownloadOptions {
  type: DownloadType;
  cameraId: string;
  cameraName: string;
  /** Site ID (número) - requerido para export */
  siteId?: number;
  /** Camera ID numérico (devices_cameras.id) - requerido para export */
  cameraIdNum?: number;
  startTime: Date;
  endTime?: Date;
  /** Para segmento: duración en segundos. Para completo: duración total. */
  durationSeconds?: number;
  /** Opciones avanzadas */
  quality?: 'original' | '1080p' | '720p' | '480p';
  format?: 'mp4' | 'avi' | 'webm';
  includeAudio?: boolean;
  includeWatermark?: boolean;
  /** Incluidos en el certificado PDF del ZIP (cadena de custodia SPOA) */
  spoa?: string;
  /** mp4 (default) | ts_zip — ZIP de segmentos .ts más rápido (sin certificado de custodia en backend) */
  packaging?: 'mp4' | 'ts_zip';
  /** requestId de la sesión HLS preparada. Habilita el fast-path: video-engine remuxa los s*.ts ya en disco sin re-leer NFS. */
  requestId?: string;
}

export interface DownloadItem {
  id: string;
  status: DownloadStatus;
  options: DownloadOptions;
  progress: number;
  /** Mientras el servidor responde sin un total conocido (sin Content-Length): barra en movimiento, no 0% estático. */
  streamIndeterminate?: boolean;
  /** Bytes por segundo (estimado o real) */
  speed?: number;
  error?: string;
  startedAt: number;
  completedAt?: number;
  /** URL del archivo una vez completado (para "Abrir") */
  fileUrl?: string;
  fileName?: string;
  /** Mensaje informativo al completar (rango verificado o advertencia de rango distinto) */
  completionMessage?: string;
  /** true si el rango exportado difiere del solicitado */
  completionWarning?: boolean;
}

const MAX_CONCURRENT = 3;
const MAX_TRANSIENT_RETRIES = 3;
const RETRY_BACKOFF_BASE_MS = 2000;

type ExportResult = Awaited<ReturnType<typeof recordingsApi.exportRecordingAsMP4>>;

type ProgressListener = (ev: {
  kind: 'generation' | 'bytes';
  percent: number;
  streamIndeterminate: boolean;
  speed?: number;
}) => void;

type InflightExport = {
  promise: Promise<ExportResult>;
  listeners: Set<ProgressListener>;
  waiters: number;
  /** true una vez que un waiter ya disparó el `<a>.click()` del blob. Evita que
   * un segundo waiter (dedupe fallado o retry) baje el MISMO MP4 dos veces. */
  browserDownloadFired: boolean;
};

function generateId(): string {
  return `dl-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function exportKey(options: DownloadOptions): string {
  return [
    options.siteId ?? '',
    options.cameraIdNum ?? '',
    options.startTime?.toISOString?.() ?? '',
    options.endTime?.toISOString?.() ?? '',
    options.quality || 'original',
    options.packaging || 'mp4',
    options.requestId?.trim() || '',
    options.spoa?.trim() || '',
  ].join('|');
}

function isTransientDownloadError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const anyErr = err as { code?: string; message?: string };
  const code = anyErr.code || '';
  if (
    code === 'ECONNABORTED' ||
    code === 'ECONNRESET' ||
    code === 'ETIMEDOUT' ||
    code === 'ERR_NETWORK' ||
    code === 'ENETUNREACH'
  ) {
    return true;
  }
  const msg = String(anyErr.message || '');
  if (/\((502|503|504)\)/.test(msg)) return true;
  if (/network error|socket hang up|network changed|timeout/i.test(msg)) return true;
  return false;
}

export interface UseDownloadManagerResult {
  downloads: DownloadItem[];
  activeCount: number;
  addDownload: (options: DownloadOptions) => string;
  cancelDownload: (id: string) => void;
  retryDownload: (id: string) => void;
  pauseDownload: (id: string) => void;
  resumeDownload: (id: string) => void;
  openFile: (id: string) => void;
  clearCompleted: () => void;
  /** Llamado internamente cuando hay que ejecutar la descarga (ej. fetch). Sustituir por API real. */
  executeDownload: (id: string, options: DownloadOptions) => Promise<void>;
}

export function useDownloadManager(): UseDownloadManagerResult {
  const { token } = useAuth();
  const [downloads, setDownloads] = useState<DownloadItem[]>([]);
  /** IDs cuya `executeDownload` ya fue disparada; evita re-ejecutar (y re-emitir el toast) si el effect vuelve a correr para el mismo item — StrictMode en dev, races entre setDownloads y updateItem, etc. */
  const startedRef = useRef<Set<string>>(new Set());
  /** Cancelación suave: quita de la UI pero no aborta el HTTP (el reintento puede reutilizar la generación en curso). */
  const cancelledIdsRef = useRef<Set<string>>(new Set());
  /** Export en vuelo por rango — cancelar + volver a pedir el mismo rango no regenera desde cero. */
  const inflightExportsRef = useRef<Map<string, InflightExport>>(new Map());
  /** Dedupe de items visibles: exportKey → id del item aún activo. Un doble-clic
   * en "Descargar" no crea dos tarjetas (que además bajarían el MP4 dos veces
   * al resolver la misma promesa compartida). Se limpia cuando el item queda
   * completed/error/cancel. */
  const activeKeyToIdRef = useRef<Map<string, string>>(new Map());

  const updateItem = useCallback(
    (id: string, patch: Partial<DownloadItem> | ((prev: DownloadItem) => Partial<DownloadItem>)) => {
      if (cancelledIdsRef.current.has(id)) return;
      setDownloads((prev) =>
        prev.map((item) => {
          if (item.id !== id) return item;
          const next = typeof patch === 'function' ? patch(item) : patch;
          return { ...item, ...next };
        })
      );
    },
    []
  );

  const executeDownload = useCallback(async (id: string, options: DownloadOptions) => {
    cancelledIdsRef.current.delete(id);
    updateItem(id, { status: 'preparing' });

    try {
      if (options.type === 'advanced' && options.siteId != null && options.cameraIdNum != null && options.endTime) {
        const currentToken = localStorage.getItem('token');
        if (!currentToken && !token) {
          throw new Error('No authentication token available. Please log in again.');
        }

        recordingsApi.setToken(currentToken || token);

        updateItem(id, {
          status: 'downloading',
          progress: 0,
          streamIndeterminate: true,
        });

        const authTok = currentToken || token || '';
        const key = exportKey(options);
        let inflight = inflightExportsRef.current.get(key);

        const onSharedProgress: ProgressListener = (ev) => {
          if (cancelledIdsRef.current.has(id)) return;
          updateItem(id, (prev) => {
            // Nunca bajar el % ni volver a indeterminado tras haber empezado a bajar bytes.
            const nextProgress = Math.max(prev.progress ?? 0, ev.percent);
            const nextIndeterminate =
              prev.streamIndeterminate === false ? false : ev.streamIndeterminate;
            return {
              progress: nextProgress,
              streamIndeterminate: nextIndeterminate,
              ...(ev.speed != null ? { speed: ev.speed } : {}),
            };
          });
        };

        if (!inflight) {
          const listeners = new Set<ProgressListener>();
          const progressSessionId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
              ? crypto.randomUUID()
              : '';
          const genPctRef = { current: 0 };
          const bytesStartedRef = { current: false };
          let pollTimer: number | undefined;
          let lastLoaded = 0;
          let lastT = Date.now();
          let lastReportedProgress = 0;

          const emit: ProgressListener = (ev) => {
            listeners.forEach((cb) => cb(ev));
          };

          if (authTok && progressSessionId) {
            pollTimer = window.setInterval(async () => {
              // Con bytes en tránsito, el % real de descarga manda: no pisar la UI
              // con el sondeo de generación (provocaba parpadeo show/hide del toast).
              if (bytesStartedRef.current) return;
              const p = await fetchExportGenerationProgress(progressSessionId, authTok);
              if (!p || bytesStartedRef.current) return;
              genPctRef.current = Math.max(genPctRef.current, Math.min(94, p.percent));
              emit({
                kind: 'generation',
                percent: genPctRef.current,
                streamIndeterminate: true,
              });
            }, 400);
          }

          const promise = (async (): Promise<ExportResult> => {
            try {
              let attempt = 0;
              let out: ExportResult | undefined;
              while (out === undefined) {
                try {
                  lastLoaded = 0;
                  lastT = Date.now();
                  lastReportedProgress = 0;
                  bytesStartedRef.current = false;
                  out = await recordingsApi.exportRecordingAsMP4(
                    options.siteId!,
                    options.cameraIdNum!,
                    options.startTime,
                    options.endTime!,
                    options.quality || 'original',
                    {
                      spoa: options.spoa,
                      packaging: options.packaging,
                      ...(options.requestId && options.requestId.trim()
                        ? { requestId: options.requestId.trim() }
                        : {}),
                      ...(progressSessionId ? { progressSessionId } : {}),
                      onProgress: (ev) => {
                        if (ev.loaded > 0) bytesStartedRef.current = true;
                        const now = Date.now();
                        const dt = Math.max(0.1, (now - lastT) / 1000);
                        const dBytes = Math.max(0, ev.loaded - lastLoaded);
                        const speed = dBytes / dt;
                        lastT = now;
                        lastLoaded = ev.loaded;
                        const hasRealPercent =
                          ev.percent != null && Number.isFinite(ev.percent);
                        const filePart = hasRealPercent
                          ? (ev.percent as number)
                          : ev.loaded > 0
                            ? Math.min(99, Math.max(genPctRef.current, 30 + Math.log10(1 + ev.loaded / (1024 * 1024)) * 22))
                            : genPctRef.current;
                        const next = Math.min(99, Math.max(filePart, lastReportedProgress, genPctRef.current));
                        // Evitar spam de setState (axios dispara onDownloadProgress muy seguido).
                        if (next - lastReportedProgress < 0.4 && ev.loaded > 0 && lastReportedProgress > 0) {
                          // Igual actualizar velocidad de vez en cuando vía umbral de tiempo:
                          if (dt < 0.35) return;
                        }
                        lastReportedProgress = next;
                        emit({
                          kind: 'bytes',
                          percent: next,
                          // Una vez hay bytes, la barra se queda en modo descarga (no vuelve a "indeterminado").
                          streamIndeterminate: !bytesStartedRef.current,
                          speed: Number.isFinite(speed) && speed > 0 ? speed : undefined,
                        });
                      },
                    }
                  );
                } catch (e) {
                  if (attempt >= MAX_TRANSIENT_RETRIES || !isTransientDownloadError(e)) {
                    throw e;
                  }
                  attempt += 1;
                  const wait = RETRY_BACKOFF_BASE_MS * Math.pow(2, attempt - 1);
                  emit({
                    kind: 'generation',
                    percent: 0,
                    streamIndeterminate: true,
                  });
                  await new Promise((r) => setTimeout(r, wait));
                }
              }
              return out!;
            } finally {
              if (pollTimer) window.clearInterval(pollTimer);
              inflightExportsRef.current.delete(key);
            }
          })();

          inflight = { promise, listeners, waiters: 0, browserDownloadFired: false };
          inflightExportsRef.current.set(key, inflight);
        }

        inflight.waiters += 1;
        inflight.listeners.add(onSharedProgress);

        let out: ExportResult;
        try {
          out = await inflight.promise;
        } finally {
          inflight.listeners.delete(onSharedProgress);
          inflight.waiters -= 1;
        }

        if (cancelledIdsRef.current.has(id)) {
          cancelledIdsRef.current.delete(id);
          // Si nadie más espera este export, liberar el blob.
          if (inflight.waiters <= 0 && out.blobUrl) {
            URL.revokeObjectURL(out.blobUrl);
          }
          return;
        }

        const fmtTime = (d: Date) => {
          const h24 = d.getHours();
          const min = d.getMinutes();
          const sec = d.getSeconds();
          const isPm = h24 >= 12;
          let h12 = h24 % 12;
          if (h12 === 0) h12 = 12;
          return `${h12}:${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')} ${isPm ? 'p. m.' : 'a. m.'}`;
        };
        const reqStart = options.startTime;
        const reqEnd = options.endTime!;
        const effStart = out.effectiveStart;
        const effEnd = out.effectiveEnd;

        let completionMessage = '';
        let completionWarning = false;
        if (effStart && effEnd) {
          const startDiffSec = Math.abs(effStart.getTime() - reqStart.getTime()) / 1000;
          const endDiffSec = Math.abs(effEnd.getTime() - reqEnd.getTime()) / 1000;
          if (startDiffSec > 30 || endDiffSec > 30) {
            completionWarning = true;
            completionMessage = `Rango exportado difiere del solicitado. Solicitado: ${fmtTime(reqStart)} – ${fmtTime(reqEnd)} | Exportado: ${fmtTime(effStart)} – ${fmtTime(effEnd)}`;
          } else {
            completionMessage = `Descarga verificada: ${fmtTime(effStart)} – ${fmtTime(effEnd)}`;
          }
        } else {
          completionMessage = `Descarga completada: ${fmtTime(reqStart)} – ${fmtTime(reqEnd)}`;
        }

        // El navegador debe recibir el archivo UNA sola vez por export, aunque
        // varios items compartan el mismo `inflight` (doble-clic, retry con la
        // misma key). El resto de waiters solo marcan su tarjeta como completada
        // reutilizando el blobUrl.
        if (!inflight.browserDownloadFired) {
          inflight.browserDownloadFired = true;
          const link = document.createElement('a');
          link.href = out.blobUrl;
          link.download = out.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => URL.revokeObjectURL(out.blobUrl), 60000);
        }

        activeKeyToIdRef.current.delete(key);

        updateItem(id, {
          status: 'completed',
          progress: 100,
          streamIndeterminate: false,
          completedAt: Date.now(),
          fileUrl: out.blobUrl,
          fileName: out.fileName,
          completionMessage,
          completionWarning,
        });
      } else {
        throw new Error('Export requires siteId, cameraIdNum, and endTime');
      }
    } catch (error: any) {
      const key = exportKey(options);
      activeKeyToIdRef.current.delete(key);
      if (cancelledIdsRef.current.has(id)) {
        cancelledIdsRef.current.delete(id);
        return;
      }
      updateItem(id, {
        status: 'error',
        error: error?.message || 'Error al exportar la grabación',
      });
    }
  }, [updateItem, token]);

  useEffect(() => {
    const running = downloads.filter(
      (d) => d.status === 'preparing' || d.status === 'downloading'
    ).length;
    if (running >= MAX_CONCURRENT) return;
    const next = downloads.find(
      (d) => d.status === 'pending' && !startedRef.current.has(d.id)
    );
    if (!next) return;
    startedRef.current.add(next.id);
    setDownloads((prev) =>
      prev.map((item) =>
        item.id === next.id ? { ...item, status: 'preparing' as const } : item
      )
    );
    executeDownload(next.id, next.options);
  }, [downloads, executeDownload]);

  const addDownload = useCallback((options: DownloadOptions): string => {
    // Dedupe: si ya hay un item activo para este mismo rango (doble-clic en
    // "Descargar", re-render que dispare el handler, etc.), devolver el id
    // existente en vez de crear una segunda tarjeta que además terminaría
    // bajando el mismo MP4 dos veces al resolver el `inflight` compartido.
    const key = exportKey(options);
    const existingId = activeKeyToIdRef.current.get(key);
    if (existingId) return existingId;
    const id = generateId();
    activeKeyToIdRef.current.set(key, id);
    const item: DownloadItem = {
      id,
      status: 'pending',
      options,
      progress: 0,
      startedAt: Date.now(),
    };
    setDownloads((prev) => [...prev, item]);
    return id;
  }, []);

  const cancelDownload = useCallback(
    (id: string) => {
      // Soft-cancel: no abort HTTP. La generación sigue y un reintento del mismo
      // rango se engancha al export en curso (sin regenerar desde 0%).
      cancelledIdsRef.current.add(id);
      startedRef.current.delete(id);
      setDownloads((prev) => {
        const gone = prev.find((d) => d.id === id);
        if (gone) {
          const k = exportKey(gone.options);
          if (activeKeyToIdRef.current.get(k) === id) {
            activeKeyToIdRef.current.delete(k);
          }
        }
        return prev.filter((d) => d.id !== id);
      });
    },
    []
  );

  const retryDownload = useCallback(
    (id: string) => {
      const item = downloads.find((d) => d.id === id);
      if (!item || item.status !== 'error') return;
      startedRef.current.delete(id);
      cancelledIdsRef.current.delete(id);
      // Re-armar el dedupe: al fallar se limpió la key para permitir un nuevo
      // download; el retry vuelve a "ocupar" la key con este id para que un
      // click extra no cree una segunda tarjeta paralela.
      activeKeyToIdRef.current.set(exportKey(item.options), id);
      updateItem(id, { status: 'pending', progress: 0, error: undefined, streamIndeterminate: undefined });
    },
    [downloads, updateItem]
  );

  const pauseDownload = useCallback((id: string) => {
    updateItem(id, { status: 'paused' });
  }, [updateItem]);

  const resumeDownload = useCallback((id: string) => {
    const item = downloads.find((d) => d.id === id);
    if (!item || item.status !== 'paused') return;
    updateItem(id, { status: 'downloading' });
    executeDownload(id, item.options);
  }, [downloads, updateItem, executeDownload]);

  const openFile = useCallback((id: string) => {
    const item = downloads.find((d) => d.id === id);
    if (item?.fileUrl) window.open(item.fileUrl, '_blank');
  }, [downloads]);

  const clearCompleted = useCallback(() => {
    setDownloads((prev) => {
      for (const d of prev) {
        if (d.status === 'completed' || d.status === 'error') startedRef.current.delete(d.id);
      }
      return prev.filter((d) => d.status !== 'completed' && d.status !== 'error');
    });
  }, []);

  const activeCount = downloads.filter(
    (d) =>
      d.status === 'pending' ||
      d.status === 'preparing' ||
      d.status === 'downloading' ||
      d.status === 'paused'
  ).length;

  return {
    downloads,
    activeCount,
    addDownload,
    cancelDownload,
    retryDownload,
    pauseDownload,
    resumeDownload,
    openFile,
    clearCompleted,
    executeDownload,
  };
}
